import React, { useState, useEffect } from "react";
import { NumericFormat } from "react-number-format";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getTranslations } from "../../../../../../services/languageUtils";
import { parseEmvPix } from "../../../../../../utils/emvPix";
import {
  isValidCpfCnpj,
  onlyDigits,
} from "../../../../../../utils/documentValidation";
import KeysManagementService from "../../../../../../services/Gowd/keysManagementService";

// Espelha PayoutMaxAmountPerTransaction em GowdService.cs:102. Acima disso o
// backend divide o payout em parcelas reaproveitando o mesmo qrCodeData
// (GowdService.cs:1962), o que reenviaria o mesmo QR Code várias vezes.
const PAYOUT_MAX_AMOUNT = 14990;

function TransferTypeCopyPasteMutual({
  currentBalance,
  sendPayment,
  loadingTranfer,
  onEmvPaymentReady,
}) {
  const [translations, setTranslations] = useState({});
  const [code, setCode] = useState("");
  const [parsed, setParsed] = useState(null);
  const [checkingDict, setCheckingDict] = useState(false);
  const [receiverName, setReceiverName] = useState("");
  const [receiverDocument, setReceiverDocument] = useState("");
  const [receiverBank, setReceiverBank] = useState("");
  const [documentSource, setDocumentSource] = useState("user");
  const [amount, setAmount] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    const loadTranslations = async () => {
      const trans = await getTranslations(
        localStorage.getItem("language") || "pt",
      );
      setTranslations(trans);
    };
    loadTranslations();
  }, []);

  const notifyError = (message) =>
    toast(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      type: "error",
      theme: "light",
    });

  const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });

  const clearPayment = () => {
    setParsed(null);
    setReceiverName("");
    setReceiverDocument("");
    setReceiverBank("");
    setDocumentSource("user");
    setAmount("");
  };

  const readCode = async () => {
    const result = parseEmvPix(code);

    if (!result.valid) {
      clearPayment();
      return notifyError(result.error);
    }

    // 1º da cascata: o que o próprio código informa.
    setParsed(result);
    setReceiverName(result.merchantName);
    setReceiverDocument("");
    setReceiverBank("");
    setDocumentSource("user");
    setAmount(result.amount ? String(result.amount) : "");

    // 2º da cascata: DICT. Só existe chave embutida em QR estático.
    if (!result.pixKey) return;

    setCheckingDict(true);
    try {
      const res = await KeysManagementService.checkDictKey(result.pixKey);
      const body = res?.data?.body || res?.data || {};
      const document = onlyDigits(body?.document?.number);

      // Desde 26/10/2026 o DICT devolve o CPF mascarado ("***.838.632-**", determinação
      // do Banco Central). Só um documento inteiro pode vir do DICT e travar o campo; com
      // CPF mascarado, o nome e o banco vêm do DICT e o documento fica com o usuário.
      if (body?.name && isValidCpfCnpj(document)) {
        setReceiverName(body.name);
        setReceiverDocument(document);
        setReceiverBank(body?.bankName || "");
        setDocumentSource("dict");
      } else if (body?.name) {
        setReceiverName(body.name);
        setReceiverBank(body?.bankName || "");
      }
    } catch (err) {
      // 3º da cascata: falhar no DICT não bloqueia — o usuário informa o documento.
      console.error("Falha ao consultar o DICT:", err);
    } finally {
      setCheckingDict(false);
    }
  };

  const isValidPayment = () => {
    const value = Number(amount);

    if (!Number.isFinite(value) || value <= 0) {
      notifyError(translations.emv_invalid_amount);
      return false;
    }
    if (value > Number(currentBalance || 0)) {
      notifyError(translations.emv_insufficient_balance);
      return false;
    }
    if (value > PAYOUT_MAX_AMOUNT) {
      notifyError(translations.emv_amount_above_limit);
      return false;
    }
    if (!receiverName.trim()) {
      notifyError(translations.emv_receiver_name_required);
      return false;
    }
    if (!isValidCpfCnpj(receiverDocument)) {
      notifyError(translations.emv_invalid_document);
      return false;
    }

    return true;
  };

  const openConfirm = () => {
    if (isValidPayment()) setShowConfirm(true);
  };

  const submitPayment = () => {
    setShowConfirm(false);
    onEmvPaymentReady({
      qrCodeData: parsed.payload,
      initiationType: parsed.isDynamic ? "DYNAMIC_QRCODE" : "STATIC_QRCODE",
      receiverName: receiverName.trim(),
      receiverDocument: onlyDigits(receiverDocument),
      amount: Number(amount),
    });
    sendPayment(2);
  };

  const nameSourceLabel = () =>
    documentSource === "dict"
      ? translations.emv_source_dict
      : translations.emv_source_code;

  const documentSourceLabel = () =>
    documentSource === "dict"
      ? translations.emv_source_dict
      : translations.emv_source_user;

  const renderCodeInput = () => (
    <div className="mainContent">
      <p className="pTitle">{translations.copyAndPaste}</p>
      <p className="pLabel">
        <br />
        {translations.emv_paste_label}
      </p>

      <input
        type="text"
        className="input"
        placeholder={translations.emv}
        value={code}
        onChange={(e) => {
          setCode(e.target.value);
          clearPayment();
        }}
      />

      <br />

      <div
        className={`buttonSend ${checkingDict ? "disabled" : ""}`}
        onClick={!checkingDict ? readCode : null}
      >
        {checkingDict ? (
          <div className="loader"></div>
        ) : (
          translations.emv_read_code
        )}
      </div>
    </div>
  );

  const renderPaymentData = () => {
    if (!parsed) return null;

    return (
      <div className="mainContent" style={{ borderRadius: "0px 8px 8px 0px" }}>
        <p className="pTitle spaceBetween">
          <span>{translations.amount}</span>
          <span style={{ fontWeight: "700" }}>
            {formatCurrency(currentBalance)}
          </span>
        </p>

        <span className="labelGray">
          {translations.emv_receiver} ({nameSourceLabel()})
        </span>
        <input
          type="text"
          className="input"
          value={receiverName}
          readOnly={documentSource === "dict"}
          onChange={(e) => setReceiverName(e.target.value)}
        />
        <br />

        <span className="labelGray">
          {translations.emv_document} ({documentSourceLabel()})
        </span>
        {documentSource !== "dict" && (
          <p className="pLabel">{translations.emv_document_required_notice}</p>
        )}
        <input
          type="text"
          className="input"
          inputMode="numeric"
          value={receiverDocument}
          readOnly={documentSource === "dict"}
          onChange={(e) =>
            setReceiverDocument(onlyDigits(e.target.value).slice(0, 14))
          }
        />
        <br />

        <span className="labelGray">{translations.how_much_transfer}</span>
        {parsed.amount ? (
          <>
            <input
              type="text"
              className="input"
              value={formatCurrency(parsed.amount)}
              readOnly
            />
            <p className="pLabel">{translations.emv_fixed_amount_notice}</p>
          </>
        ) : (
          <NumericFormat
            className="input"
            value={amount}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale
            prefix={translations.currency_prefix}
            onValueChange={(values) => setAmount(values.value)}
          />
        )}

        {parsed.isDynamic && (
          <p className="pLabel">{translations.emv_dynamic_amount_warning}</p>
        )}

        <br />

        <div
          className={`buttonSend ${loadingTranfer ? "disabled" : ""}`}
          onClick={!loadingTranfer ? openConfirm : null}
        >
          {loadingTranfer ? (
            <div className="loader"></div>
          ) : (
            translations.emv_continue
          )}
        </div>
      </div>
    );
  };

  const confirmRow = (label, value) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "8px 0",
        borderBottom: "1px solid #f0f0f0",
        gap: "16px",
      }}
    >
      <span>
        <strong>{label}</strong>
      </span>
      <span style={{ textAlign: "right" }}>{value}</span>
    </div>
  );

  const renderConfirmModal = () => (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "20px",
          minWidth: "350px",
          maxWidth: "90%",
          boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h3
          style={{ fontSize: "20px", fontWeight: "bold", marginBottom: "15px" }}
        >
          {translations.emv_confirm_title}
        </h3>

        <div style={{ borderTop: "1px solid #f0f0f0" }}>
          {confirmRow(
            `${translations.emv_receiver} (${nameSourceLabel()})`,
            receiverName,
          )}
          {confirmRow(
            `${translations.emv_document} (${documentSourceLabel()})`,
            receiverDocument,
          )}
          {receiverBank && confirmRow(translations.emv_bank, receiverBank)}
          {confirmRow(
            translations.emv_qr_type,
            parsed.isDynamic
              ? translations.emv_qr_dynamic
              : translations.emv_qr_static,
          )}
          {confirmRow(translations.amount, formatCurrency(amount))}
        </div>

        {parsed.isDynamic && (
          <p className="pLabel" style={{ marginTop: "12px" }}>
            {translations.emv_dynamic_amount_warning}
          </p>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "20px",
            gap: "10px",
          }}
        >
          <button
            onClick={() => setShowConfirm(false)}
            style={{
              backgroundColor: "white",
              border: "1px solid #aaa",
              color: "#555",
              borderRadius: "6px",
              padding: "8px 16px",
              cursor: "pointer",
            }}
          >
            {translations.emv_cancel}
          </button>
          <button
            onClick={submitPayment}
            style={{
              backgroundColor: "white",
              border: "1px solid #1c787e",
              color: "#1c787e",
              borderRadius: "6px",
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {translations.emv_confirm_action}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <span className="desktop">
        {renderCodeInput()}
        {renderPaymentData()}
      </span>

      <span className="mobile">
        <div>
          {renderCodeInput()}
          {renderPaymentData()}
        </div>
      </span>

      {showConfirm && parsed && renderConfirmModal()}

      <ToastContainer />
    </div>
  );
}

export default TransferTypeCopyPasteMutual;
